import type { MDXComponents } from 'mdx/types';
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { isValidElement } from 'react';
import LivePeriod from './widgets/LivePeriod';
import ContractRef from './widgets/ContractRef';
import ContractsArchitecture from './widgets/ContractsArchitecture';
import DataFlowDiagram from './widgets/DataFlowDiagram';
import TxExample from './widgets/TxExample';
import LastUpdated from './widgets/LastUpdated';
import Mermaid from './Mermaid';

interface CodeChildProps {
  className?: string;
  children?: ReactNode;
}

/**
 * Detect a mermaid fenced code block. MDX emits these as:
 *   <pre><code className="language-mermaid">...source...</code></pre>
 */
function getMermaidSource(preChildren: ReactNode): string | null {
  if (!isValidElement(preChildren)) return null;
  const props = (preChildren as ReactElement<CodeChildProps>).props;
  const cls = props.className ?? '';
  if (!cls.split(/\s+/).includes('language-mermaid')) return null;
  const raw = props.children;
  return typeof raw === 'string' ? raw : String(raw ?? '');
}

/**
 * Components made available inside every MDX file. Authors can write
 * <LivePeriod /> or <ContractRef name="MethaToken" /> directly in markdown,
 * plus ```mermaid fenced code blocks which render as live SVG diagrams.
 *
 * - `table`: wide tables get a horizontal scroll container so they don't
 *   overflow the article column on narrow viewports.
 * - `pre`:   ```mermaid blocks get routed to <Mermaid /> (lazy-loaded);
 *   all other code blocks render with the default <pre>.
 */
export const mdxComponents: MDXComponents = {
  LivePeriod,
  ContractRef,
  ContractsArchitecture,
  DataFlowDiagram,
  TxExample,
  LastUpdated,
  table: (props: HTMLAttributes<HTMLTableElement>) => (
    <div className="md-table-wrap">
      <table {...props} />
    </div>
  ),
  pre: (props: HTMLAttributes<HTMLPreElement>) => {
    const mermaidSource = getMermaidSource(props.children);
    if (mermaidSource !== null) {
      return <Mermaid source={mermaidSource.replace(/\n+$/, '')} />;
    }
    return <pre {...props} />;
  },
};
